import '@radix-ui/themes/styles.css';
import './extensions/array';
import './styles/index.css';

import { Theme } from '@radix-ui/themes';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Agentation } from 'agentation';
import { ConfigProvider } from 'antd';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import TagManager from 'react-gtm-module';

import { TooltipProvider } from '@/components/Tooltip/TooltipProvider';
import SynclyApp from '@/features/App/AppBase';
import FrontPlugin from '@/features/FrontPlugin/PluginBase';
import { FrontContextProvider } from '@/features/FrontPlugin/provider/frontContext';
import useAuthInit from '@/hooks/domain/App/useAuthInit';
import { useSocketInit } from '@/hooks/domain/App/useSocket';
import useTrackInit from '@/hooks/domain/App/useTrackInit';
import useFrontPlugin from '@/hooks/domain/useFrontPlugin';
import { useWorkspaceLanguageSync } from '@/i18n/useWorkspaceLanguageSync';
import { queryClient } from '@/shared/lib/query-client';
import useCustomAntdTheme from '@/styles/useCustomAntdTheme';

import { AuthProvider } from './features/App/auth-provider';
import { CopositeThemeProvider } from './styles/CompositeThemeProvider';
import { GlobalStyles } from './styles/style.globals';

Chart.register(...registerables, ChartDataLabels);

function App() {
  const { isFrontPlugin } = useFrontPlugin();
  const customAntdTheme = useCustomAntdTheme();

  useAuthInit();
  useTrackInit();
  useSocketInit();
  useWorkspaceLanguageSync();
  // useDatadogInit();

  useEffect(() => {
    if (import.meta.env.VITE_PUBLIC_ENVIRONMENT !== 'prod') return;
    const gtmId = import.meta.env.VITE_PUBLIC_GTM_ID;
    if (!gtmId) return;
    TagManager.initialize({ gtmId });
  }, []);

  return (
    <Theme>
      <ConfigProvider
        theme={customAntdTheme}
        getPopupContainer={() => document.querySelector('#dropdown-layer')!}
      >
        <CopositeThemeProvider>
          <DndProvider backend={HTML5Backend}>
            <AuthProvider>
              <QueryClientProvider client={queryClient}>
                <TooltipProvider>
                  <GlobalStyles />

                  {isFrontPlugin ? (
                    <FrontContextProvider>
                      <FrontPlugin />
                    </FrontContextProvider>
                  ) : (
                    <SynclyApp />
                  )}

                  <ReactQueryDevtools
                    initialIsOpen={false}
                    buttonPosition="bottom-right"
                  />
                </TooltipProvider>
              </QueryClientProvider>
            </AuthProvider>
          </DndProvider>
        </CopositeThemeProvider>
      </ConfigProvider>
      {process.env.NODE_ENV === 'development' && <Agentation />}
    </Theme>
  );
}

export default App;
